import React, { FC, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import { mapCustomFieldsToForm } from "go-app/components/CustomFields";
import CustomFieldsForm from "go-app/components/CustomFields/CustomFieldsForm";
import handleError from "go-app/services/errors";
import { SaveAndAddButtons, TranslationType } from "go-core/components/SaveAndAddButtons";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { CustomValidationConfig, CustomValidationConstraint } from "go-form/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { ProductCategoryApi } from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";

interface Props {
	productCategory?: ProductCategoryApi;
	customFieldsConfig: CustomFieldTemplateApi[];
}

const ProductCategoryForm: FC<Props> = (props) => {
	const { productCategory, customFieldsConfig } = props;
	const [saveLoading, setSaveLoading] = useState(false);
	const [saveAndAddLoading, setSaveAndAddLoading] = useState(false);
	const saveAndAddMode = useRef(false);
	const organization = useSelector(selectOrganization);
	const customFields = productCategory?.custom_fields;
	const history = useHistory();
	const { t } = useTranslation();
	const form = useForm<ProductCategoryApi>({
		criteriaMode: "all",
		defaultValues: {
			...productCategory,
			custom_fields: mapCustomFieldsToForm(customFieldsConfig, customFields || []),
		},
	});
	const {
		register,
		formState,
		formState: { errors },
		handleSubmit,
		reset,
		watch,
		setError,
	} = form;
	const { addFlash, addSuccessFlash } = useFlash();
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	const onSubmit = handleSubmit(async (data: ProductCategoryApi) => {
		if (!validateCustomErrors()) {
			return;
		}

		try {
			saveAndAddMode.current ? setSaveAndAddLoading(true) : setSaveLoading(true);
			if (productCategory?.id) {
				const params: Record<string, any> = { include: "custom_fields" };
				const res = await api.organization().updateProductCategory(data, params);
				reset({
					...res,
					custom_fields: mapCustomFieldsToForm(customFieldsConfig, res.custom_fields || []),
				});
				if (saveAndAddMode.current) history.push(`/${organization.id}/products/categories/new`);
			} else {
				const res = await api.organization().createProductCategory(data);
				if (saveAndAddMode.current) reset();
				else history.push(`/${organization.id}/products/categories/${res.id}/edit`);
			}
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
		} catch (err) {
			handleError.form(err, setError, addFlash);
		} finally {
			setSaveLoading(false);
			setSaveAndAddLoading(false);
			saveAndAddMode.current = false;
		}
	});

	const nameValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch },
		}),
		[setErrors, watch]
	) satisfies CustomValidationConfig;

	const saveAndAddClickHandler = () => {
		saveAndAddMode.current = true;
		onSubmit();
	};

	return (
		<FormDirty
			buttonSubmitCustom={
				<SaveAndAddButtons
					saveLoading={saveLoading}
					saveAndAddLoading={saveAndAddLoading}
					onSaveAndAddClick={saveAndAddClickHandler}
					translationType={TranslationType.FEMALE}
				/>
			}
			formState={formState}
			onSubmit={onSubmit}
			loading={saveLoading}
			key="product-category-form"
		>
			<fieldset className="form-group">
				<h5>{t("common.word.basic_info")}</h5>
				<div className="row">
					<div className="col-md-12">
						<FormInput
							name="name"
							label={t("common.word.name", { ns: "lib" })}
							errors={errors}
							register={register}
							customValidationConfig={nameValidationConfig}
						/>
					</div>
				</div>
			</fieldset>
			{customFieldsConfig && customFieldsConfig.length > 0 && (
				<fieldset className="form-group">
					<h5>{t("common.word.advanced", { ns: "lib" })}</h5>
					<CustomFieldsForm
						customFieldsConfig={props.customFieldsConfig}
						customFields={customFields || []}
						form={form}
						setErrors={setErrors}
					/>
				</fieldset>
			)}
		</FormDirty>
	);
};

export default ProductCategoryForm;
